import React from 'react';
import { css } from '@emotion/core';
import {
  EventDTO,
  IndicatorDTOHack,
  SloQualityResultDTO,
  SloType,
} from '@atlassiansox/phobos-client-ts';
import { SimpleTag } from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';
import Lozenge from '@atlaskit/lozenge';
import isNil from 'lodash/isNil';
import * as colors from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import upperFirst from 'lodash/upperFirst';

import { InterimResults, PerformanceWithEvents } from '@/ui';
import { useSLOGraphController } from '@/controllers';
import { Item, useActions } from '@/components/primitive';
import { formatDateForDisplay } from '@/utils';
import { DateDisplayFormats } from '@/constants';
import {
  SPACE_COZY,
  SPACE_COMFORTABLE,
  SPACE_MINIMUM,
  SPACE_COMPACT,
  flexWrap,
  flexStart,
} from '@/styles';

import { slaCoveredExperienceKey, slaCriticalPathKey } from '../../forms/slo/fields/tags-input';

import ExpandedGraph from './graph';
import { LOW_TRAFFIC_DEFAULT_THRESHOLD } from './low-traffic-helper/constants';
import { SLOLifecycleStatus } from './types';

const overviewStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${SPACE_COZY}px;
  margin-top: ${SPACE_COMFORTABLE}px;
`;

const overviewInnerStyles = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${SPACE_COZY}px;
`;

const tagGroupStyle = css`
  margin: -${SPACE_MINIMUM}px;
  span {
    max-width: 100%;
    font-size: 12px;
    ${flexStart};
  }
`;

const tagMultiGroupStyle = css`
  padding: ${SPACE_MINIMUM}px ${SPACE_COMPACT}px;
  background-color: ${token('color.background.accent.gray.subtlest', colors.N20)};
  margin: 12px ${SPACE_MINIMUM}px ${SPACE_MINIMUM}px ${SPACE_MINIMUM}px;
  border-radius: ${SPACE_MINIMUM}px;
  ${flexStart};
  ${flexWrap};
`;

const tagMultiGroupTitleStyle = css`
  line-height: ${SPACE_COZY}px;
  padding: ${SPACE_MINIMUM}px;
  font-weight: normal;
`;

const enabledAlertsTags = css`
  padding-right: ${SPACE_MINIMUM}px;
`;

const pollinatorList = css`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${SPACE_MINIMUM / 2}px;
  align-items: flex-start;
`;

const pollinatorListItem = css`
  font-size: 12px;
  line-height: 16px;
  word-break: break-word;
  overflow-wrap: anywhere;
  /* Optional subtle background without horizontal padding so text stays flush */
  background: ${token('color.background.neutral.subtle', colors.N20)};
  border-radius: 3px;
  padding: 2px 0; /* no left/right padding keeps alignment */
  width: 100%;
`;

const POLLINATOR_URL = 'https://pollinator.prod.atl-paas.net';
const POLLINATOR_CHECK_URL = `${POLLINATOR_URL}/checks/`;
const POLLINATOR_ARCHETYPE_URL = `${POLLINATOR_URL}/archetypes/`;

type SloPageDataProps = {
  name: string;
  publicId: string;
  indicator?: IndicatorDTOHack;
  interimResults?: InterimResults;
  performance?: PerformanceWithEvents;
  quality?: SloQualityResultDTO;
  objective: number;
  tags: { [key: string]: string | undefined };
  type: SloType;
  serviceId?: string;
  suppressLowTrafficAlerts?: boolean;
  isUltraSteepBurnEnabled?: boolean;
  lowTrafficThreshold?: number | null;
  isSloGroupMemberPage?: boolean;
  enabledAlerts?: string;
  sloLifecycleStatus?: SLOLifecycleStatus;
  pollinatorUuids?: string[];
  pollinatorArchetypeUuids?: string[];
  events: EventDTO[];
};

const SloPageData: React.FC<SloPageDataProps> = ({
  events,
  name,
  publicId,
  indicator,
  interimResults,
  performance,
  quality,
  objective,
  tags,
  type,
  serviceId,
  suppressLowTrafficAlerts,
  isUltraSteepBurnEnabled,
  lowTrafficThreshold,
  isSloGroupMemberPage,
  enabledAlerts,
  sloLifecycleStatus,
  pollinatorUuids,
  pollinatorArchetypeUuids,
}) => {
  const { onEdit } = useActions(publicId, name, 'slo');

  // TODO: replace this Map by using SLO endpoint returning value like
  // getAllowedTags endpoint does
  const friendlyKeyMap: any = {
    [slaCoveredExperienceKey]: 'Cloud SLA Covered Experience',
    [slaCriticalPathKey]: 'Critical Path SLO',
    env: 'Environment',
    cohort: 'Cohort',
  };

  const graph = useSLOGraphController(
    objective,
    indicator,
    performance,
    publicId,
    quality,
    type,
    interimResults,
    sloLifecycleStatus
  );

  const hasDataPoints = !!performance?.dataPoints.length;

  return (
    <div data-testid="slo-page-graph">
      <ExpandedGraph
        warnings={graph.warnings}
        dataPoints={graph.dataPoints}
        error={graph.error}
        errorBudgetDataPoints={graph.errorBudgetDataPoints}
        errorBudgetDomain={graph.errorBudgetDomain}
        interimResults={interimResults}
        status={graph.status}
        onEdit={onEdit}
        publicId={publicId}
        selectionCallback={null}
        performance={performance}
        lowTrafficThreshold={lowTrafficThreshold}
        events={events}
      />
      <br />
      <div css={[overviewStyles]}>
        <h4>Overview</h4>
        <div css={overviewInnerStyles}>
          {performance ? (
            <>
              <Item title="Last successful datapoint sync (UTC)">
                <code>
                  {performance.latestDataPointTimestamp
                    ? formatDateForDisplay(
                        performance.latestDataPointTimestamp,
                        DateDisplayFormats.TIME_WITH_NUMERIC_DATE
                      )
                    : 'N/A'}
                </code>
              </Item>
              <Item title="Reporting period starting (UTC)">
                <code>
                  {hasDataPoints
                    ? formatDateForDisplay(
                        performance.dataPoints[0].timestamp,
                        DateDisplayFormats.TIME_WITH_NUMERIC_DATE
                      )
                    : 'N/A'}
                </code>
              </Item>
              {indicator && indicator.dataSourceQueryReason && (
                <Item title="Last collection type">
                  <code>{indicator.dataSourceQueryReason}</code>
                </Item>
              )}
              <Item title="Total events (last 28 days)">
                <code>
                  {interimResults?.totalCount ?? performance.badCount + performance.goodCount}
                </code>
              </Item>
              <Item title="Total success events (last 28 days)">
                <code>{interimResults?.goodCount ?? performance.goodCount}</code>
              </Item>
              <Item title="Total failure events (last 28 days)">
                <code>{interimResults?.badCount ?? performance.badCount}</code>
              </Item>
              <Item title="Tags">
                {tags && Object.keys(tags).length > 0 ? (
                  <div css={tagGroupStyle}>
                    {(tags[slaCoveredExperienceKey] ?? '').length > 0 && (
                      <div css={tagMultiGroupStyle}>
                        <span css={tagMultiGroupTitleStyle}>
                          {friendlyKeyMap[slaCoveredExperienceKey]} ({slaCoveredExperienceKey}):
                        </span>
                        {(tags[slaCoveredExperienceKey] ?? '').split(',').map(value => (
                          <SimpleTag key={value} text={value} />
                        ))}
                      </div>
                    )}
                    <TagGroup>
                      {Object.entries(tags).map(([tagKey, tagValue]) => {
                        if (tagValue === undefined) {
                          throw new Error('Tag value cannot be undefined in returned map');
                        }
                        if (tagKey === slaCoveredExperienceKey) {
                          return null;
                        }
                        const tagKeyFriendly = friendlyKeyMap[tagKey] ?? upperFirst(tagKey);
                        return (
                          <SimpleTag
                            testId="slo-tag-simple-tag"
                            key={`tag-simple-tag-${tagKey}`}
                            text={`${tagKeyFriendly} (${tagKey}):${tagValue}`}
                          />
                        );
                      })}
                    </TagGroup>
                  </div>
                ) : (
                  <code>No tags set.</code>
                )}
              </Item>
              <Item title="Pollinators">
                {pollinatorUuids && pollinatorUuids.length > 0 ? (
                  <ul css={pollinatorList}>
                    {pollinatorUuids.map(id => (
                      <li key={id} css={pollinatorListItem}>
                        <a
                          href={`${POLLINATOR_CHECK_URL}${encodeURIComponent(id)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {id}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <code>-</code>
                )}
              </Item>

              <Item title="Pollinator Archetypes">
                {pollinatorArchetypeUuids && pollinatorArchetypeUuids.length > 0 ? (
                  <ul css={pollinatorList}>
                    {pollinatorArchetypeUuids.map(id => (
                      <li key={id} css={pollinatorListItem}>
                        <a
                          href={`${POLLINATOR_ARCHETYPE_URL}${encodeURIComponent(id)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {id}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <code>-</code>
                )}
              </Item>
              {serviceId && (
                <Item title="service id">
                  <code>{serviceId}</code>
                </Item>
              )}
              <>
                <Item title="low traffic threshold">
                  <code>
                    {isNil(lowTrafficThreshold)
                      ? LOW_TRAFFIC_DEFAULT_THRESHOLD
                      : lowTrafficThreshold}
                  </code>
                </Item>
                <Item title="suppress low traffic alerts">
                  <Lozenge isBold={false}>{suppressLowTrafficAlerts ? 'yes' : 'no'}</Lozenge>
                </Item>
              </>
              {isSloGroupMemberPage && enabledAlerts && (
                <Item title="enabled alerts">
                  {enabledAlerts.split(',').map((text, _) => (
                    <span key={text} css={enabledAlertsTags}>
                      <Lozenge>{text}</Lozenge>
                    </span>
                  ))}
                </Item>
              )}
              {!isSloGroupMemberPage && (
                <Item title="enable ultra steep (5-minute) burn alert">
                  <Lozenge isBold={false}>{isUltraSteepBurnEnabled ? 'yes' : 'no'}</Lozenge>
                </Item>
              )}
            </>
          ) : (
            <Item title="N/A">
              <code>No performance data available.</code>
            </Item>
          )}
        </div>
      </div>
    </div>
  );
};

export default SloPageData;
